import React, { useEffect, useState } from "react";

import { IconRefreshCw } from "@frontend/common";

import { useTopicType } from "@/contexts/TopicTypeContext";
import BorderBox from "@atoms/BorderBox/BorderBox";
import FilterTag from "@atoms/FilterTag/FilterTag";
import IconCheck from "@images/common/icon-filter-check-white.svg";
import XMarkIcon from "@images/pressList/icon-x-mark-red.svg";
import { usePublicationListFilterState } from "@stores/publicationList";

import TopicTypeFilterSection from "./components/TopicTypeFilterSection/TopicTypeFilterSection";
import * as S from "./TopicTypeFilter.style";
import { StatusFilterList, StatusFilterValue } from "./topicTypeFilter.types";

interface Props {
  closeModal: () => void;
}

const TopicTypeFilter = ({ closeModal }: Props) => {
  const [publicationListFilter, setPublicationListFilter] =
    usePublicationListFilterState();
  const { topicType } = publicationListFilter;
  const { topicTypeData, isLoading } = useTopicType();

  const [topicTypeFilterList, setTopicTypeFilterList] =
    useState<StatusFilterList>({} as StatusFilterList);
  const [isAllChecked, setIsAllChecked] = useState(false);

  const handleReset = () => {
    if (topicTypeData) {
      const resetList = Object.entries(topicTypeData).reduce(
        (result, [key, value]) => {
          return {
            ...result,
            [key]: [{ code: key, name: value, isChecked: false }],
          };
        },
        {} as StatusFilterList,
      );
      setTopicTypeFilterList(resetList);
      setIsAllChecked(false);
    }
  };

  const handleApplyTopicType = () => {
    const updatedTopicTypeFilterList = Object.entries(
      topicTypeFilterList,
    ).flatMap(([, value]) =>
      (value as StatusFilterValue[]).filter((item) => item.isChecked),
    );
    const newTopicTypeFilter = updatedTopicTypeFilterList.map(
      (item) => item.code,
    );
    setPublicationListFilter({
      ...publicationListFilter,
      topicType: newTopicTypeFilter,
      page: 1,
    });
    closeModal();
  };

  useEffect(() => {
    if (topicTypeData && !isLoading) {
      const initTopicTypeFilterList: StatusFilterList = Object.entries(
        topicTypeData,
      ).reduce((result, [key, value]) => {
        const updatedList = [
          {
            code: key,
            name: value,
            isChecked: topicType.includes(key),
          },
        ];
        return { ...result, [key]: updatedList };
      }, {} as StatusFilterList);
      setTopicTypeFilterList(initTopicTypeFilterList);
      setIsAllChecked(false);
    }
  }, [topicTypeData, topicType, isLoading]);

  const totalCheckedTopicType = Object.entries(topicTypeFilterList)
    .map(
      ([, value]) =>
        (value as StatusFilterValue[]).filter((item) => item.isChecked).length,
    )
    .reduce((acc, cur) => acc + cur, 0);

  const confirmButtonText =
    totalCheckedTopicType > 1
      ? "Apply Topic Type Filters"
      : "Apply Topic Type Filter";

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.currentTarget;

    const updatedTopicTypeFilterList = Object.fromEntries(
      Object.entries(topicTypeFilterList).map(([key, value]) => [
        key,
        value.map((item) => ({ ...item, isChecked: checked })),
      ]),
    );

    setTopicTypeFilterList(updatedTopicTypeFilterList);
    setIsAllChecked(checked);
  };

  useEffect(() => {
    if (!topicTypeFilterList) return;

    const totalItems = Object.values(topicTypeFilterList).flat().length;
    const checkedItems = Object.values(topicTypeFilterList)
      .flat()
      .filter((item) => item.isChecked).length;

    setIsAllChecked(totalItems > 0 && totalItems === checkedItems);
  }, [topicTypeFilterList]);

  if (isLoading) {
    return (
      <S.StatusFilterWrapper>
        <BorderBox padding="16">
          <div>Loading topic types...</div>
        </BorderBox>
      </S.StatusFilterWrapper>
    );
  }

  return (
    <S.StatusFilterWrapper>
      <BorderBox padding="16">
        <S.StatusFilterContentWrapper>
          <S.StatusFilterTitleRow>
            <S.FilterResetButton
              onClick={handleReset}
              disabled={totalCheckedTopicType < 1}
            >
              {totalCheckedTopicType < 1 ? (
                <IconRefreshCw width={20} height={20} color="#a5a5a5" />
              ) : (
                <IconRefreshCw width={20} height={20} color="#515C6C" />
              )}
              <S.FilterResetButtonText>Reset</S.FilterResetButtonText>
            </S.FilterResetButton>
            <S.ClassificationCloseButton
              type="button"
              onClick={() => closeModal()}
            >
              <S.ClassificationXMark>
                <img src={XMarkIcon} alt="close filter" />
              </S.ClassificationXMark>
              <S.ClassificationCloseText>Close</S.ClassificationCloseText>
            </S.ClassificationCloseButton>
          </S.StatusFilterTitleRow>
          <S.StatusFilterTitleWrapper>
            <S.StatusFilterTitle>Topic Type</S.StatusFilterTitle>
            <FilterTag
              name="topicType"
              value="all"
              onChange={handleSelectAll}
              checked={isAllChecked}
              isAll
            >
              Select All
            </FilterTag>
          </S.StatusFilterTitleWrapper>
          <S.StatusFilterRow>
            {Object.entries(topicTypeFilterList).map(([key, value]) => (
              <S.StatusFilterSectionWrapper key={`topic-type-section-${key}`}>
                <TopicTypeFilterSection
                  statusSectionList={value as StatusFilterValue[]}
                  filterKey={key}
                  statusFilterList={topicTypeFilterList}
                  setStatusFilterList={setTopicTypeFilterList}
                />
              </S.StatusFilterSectionWrapper>
            ))}
          </S.StatusFilterRow>
          <S.ConfirmButtonWrapper>
            <S.ConfirmButton onClick={handleApplyTopicType}>
              <img src={IconCheck} alt="filter check icon" />
              <S.ConfirmButtonText>{confirmButtonText}</S.ConfirmButtonText>
            </S.ConfirmButton>
          </S.ConfirmButtonWrapper>
        </S.StatusFilterContentWrapper>
      </BorderBox>
    </S.StatusFilterWrapper>
  );
};

export default TopicTypeFilter;
